/**
 * Captain Mesh Application Service
 * 
 * Provides clean application-level operations for React components.
 * 
 * The single most critical rule of CaptainPAD:
 * User Action -> Validate -> Persist Local Event -> Apply Materializer -> 
 * (If server: send server) else (If mesh: replicate) else (stay LOCAL_ONLY).
 */

import { offlineDb } from "../../../db/offlineDb";
import { saveAndApplyEvent } from "../db";
import { networkState, NetworkMode } from "../networkState";
import { peerManager } from "../transport/peerManager";
import { uuid } from "@/lib/uuid";
import { getActiveActorSession, signEventPayload, getEventSigningPayloadString } from "../transport/identity";

export class MeshService {

  constructor() {
    // Optimistic mesh sync when a peer connects
    peerManager.onPeerConnected = async (peerId) => {
      try {
        const { computeLocalFrontiers } = await import("../transport/cursors");
        const frontiers = await computeLocalFrontiers();
        peerManager.sendToPeer(peerId, { type: "SYNC_REQUEST", frontiers });
      } catch (err) {
        console.error("Failed to send SYNC_REQUEST", err);
      }
    };

    // Expose devtools for E2E tests
    if (typeof window !== 'undefined') {
      window._meshDevtools = {
        peerManager,
        getEvents: async () => {
          return await offlineDb.captain_events.toArray();
        },
        getMaterializedOrder: async (id) => {
          return await offlineDb.captain_materialized.get(id);
        },
        broadcastRaw: async (event) => {
          peerManager.broadcast({ type: "EVENT_BATCH", events: [event] });
        },
        forceDisconnect: () => {
          // close(), not destroy() — RTCPeerConnection has no destroy(), and the
          // old call only avoided throwing when there was nothing to disconnect,
          // i.e. every "partition" it ever simulated was a no-op on an empty map.
          // `suspended` first: a real partition drops packets without running
          // cleanup code, so the transport's own auto-heal must not repair the
          // outage the test is simulating.
          peerManager.suspended = true;
          peerManager.channels.forEach(ch => { try { ch.close(); } catch { /* already closed */ } });
          peerManager.channels.clear();
          peerManager.peers.forEach(pc => { try { pc.close(); } catch { /* already closed */ } });
          peerManager.peers.clear();
        },
        reconnect: () => {
          // start() alone could not reconnect: new links are only ever made on
          // PEER_JOINED, and after a drop both sides are still "joined" on the
          // hub, so that event never fires again. Reconnection comes from the
          // transport's memory of who it met.
          peerManager.start();
          peerManager.reconnectKnownPeers();
        },

        /**
         * One call that answers "which link in the chain is broken?".
         *
         * A failing e2e assertion on the DOM cannot distinguish an event that
         * never arrived from one that arrived, materialized, and simply never
         * reached React. This walks the same chain the product does and reports
         * each stage separately, so a test can name the FIRST broken invariant
         * instead of timing out on the last one.
         *
         * `projection` deliberately re-implements the selector in
         * useCaptainAllTablesState against Dexie directly. If `materialized` is
         * populated and `projection.orderForTable` is null, the matching logic
         * is wrong; if both are populated and the DOM still says vacant, the
         * liveQuery subscription is not firing and the bug is in React, not the
         * mesh.
         */
        probe: async (aggregateId, tableDbId) => {
          const events = await offlineDb.captain_events.toArray();
          const forAggregate = events.filter(e => e.aggregate_id === aggregateId);
          const record = aggregateId
            ? await offlineDb.captain_materialized.get(aggregateId)
            : null;
          const all = await offlineDb.captain_materialized.toArray();
          const states = all.map(m => ({ ...m.state, order_id: m.state.id }));
          // Mirrors useCaptainAllTablesState.orderForTable exactly — the probe
          // must report what the real selector would do, not a friendlier one.
          const match = states.find(s => s.table_id === tableDbId);

          return {
            correlationId: window.__M15_CID || null,
            networkMode: networkState.getMode(),
            channels: Array.from(peerManager.channels?.keys?.() || []),
            events: {
              total: events.length,
              forAggregate: forAggregate.length,
              byType: forAggregate.reduce((acc, e) => {
                acc[e.type] = (acc[e.type] || 0) + 1;
                return acc;
              }, {}),
              unsynced: events.filter(e => e._sync_state !== "server_acked").length,
            },
            materialized: record
              ? {
                  table_id: record.state.table_id,
                  table_id_type: typeof record.state.table_id,
                  status: record.state.status,
                  itemNames: Object.values(record.state.items || {}).map(i => i.item_name),
                }
              : null,
            projection: {
              aggregateCount: states.length,
              lookedUpTableDbId: tableDbId,
              orderForTable: match
                ? { table_id: match.table_id, status: match.status }
                : null,
            },
            dom: {
              tone:
                document
                  .querySelector(`[data-testid="table-Table ${tableDbId}-button"]`)
                  ?.getAttribute("data-tone") || null,
            },
          };
        },
      };
    }
  }

  /**
   * Main entry point for ALL operational mutations in CaptainPAD.
   * Online or offline, the UI calls this.
   */
  async submitEvent(aggregateId, type, payload) {
    // Never ask Dexie for an empty key: it throws "Invalid argument to
    // Table.get()", which reached the captain as nothing at all (2026-10-14).
    if (!aggregateId) throw new Error("this table's order is not ready on this pad yet");
    // Get the current materialized state to find the causal parent
    const currentState = await this.getOrderState(aggregateId);
    const parentId = currentState ? currentState._last_event_id : null;

    // The revision is not decoration: the server's replay sorts by
    // result_revision FIRST (created_at is only the tie-break). Events sent
    // without one sort as revision 0 — which put a live "+1" delta BEFORE the
    // adopted ORDER_STARTED (revision 1) it amends, so the replay saw a delta
    // for a line that did not exist yet and silently dropped it: the tablet
    // said 2, the bill said 1.
    const parentRevision = (currentState && currentState.revision) || 0;

    // 1. Create Local Event
    const session = getActiveActorSession();
    if (!session) throw new Error("No active actor session");

    const event = {
      event_id: uuid(),
      aggregate_id: aggregateId,
      parent_event_id: parentId,
      parent_revision: parentRevision,
      result_revision: parentRevision + 1,
      type: type,
      payload: payload,
      created_at: new Date().toISOString(),
      device_id: session.device_id,
      // String, not the raw int: MeshEventIn types actor_id as Optional[str]
      // with extra="forbid", and pydantic v2 does not coerce int -> str, so an
      // int here 422s the whole aggregate. Both signing payloads stringify it
      // (JS join, python str()), so the signature is unaffected either way.
      actor_id: String(session.authorization.user_id),
      actor_authorization: session.authorization
    };
    
    event.actor_signature = await signEventPayload(getEventSigningPayloadString(event));

    // 2. Validate (Schema / Basic Domain Rules)
    if (!aggregateId || !type) {
      throw new Error("Invalid event format");
    }

    // 3 & 4. Persist local event and apply reducer/materialized state
    // This is the invariant. Data is durably saved immediately.
    await saveAndApplyEvent(event);

    // 5. Route to the authoritative server when there is one.
    //
    // There is no separate "push one event" path any more. Online and offline
    // both go through reconciliation, which replays the aggregate's whole log
    // into the real order commands and is idempotent on event_id. One path means
    // an order taken online and the same order taken offline become the same
    // rows by the same route — and there is no second set of business rules to
    // drift out of step.
    const mode = networkState.getMode();

    if (mode === NetworkMode.SERVER_CONNECTED || mode === NetworkMode.SERVER_DEGRADED) {
      try {
        const { syncService } = await import("./syncService");
        await syncService.reconcileAggregate(aggregateId);
      } catch (err) {
        // Stays local and un-acknowledged; the failover monitor will flush it
        // when the server is back. Nothing is lost — the event is already in
        // IndexedDB by step 3.
        console.warn("[MeshService] server reconcile deferred", err);
      }
    }


    // Replicate to peers whenever there are any — including while the server is
    // connected, since the reducer is idempotent and the DataChannel beats a
    // round trip. LOCAL_ONLY is the exception: it means no server AND no peers,
    // so there is nobody to send to. A peer that connects later catches up
    // through the SYNC_REQUEST frontier exchange in the constructor above, so
    // skipping here loses nothing.
    if (mode !== NetworkMode.LOCAL_ONLY) {
      peerManager.broadcast({
        type: "EVENT_BATCH",
        events: [event]
      });
    }

    return event;
  }

  /**
   * Retrieve current materialized state.
   */
  async getOrderState(aggregateId) {
    if (!aggregateId) return null;
    const state = await offlineDb.captain_materialized.get(aggregateId);
    return state ? state.state : null;
  }

  /**
   * Retrieve all local events (useful for diagnostic UI)
   */
  async getAllEvents() {
    const events = await offlineDb.captain_events.toArray();
    console.log("[MeshService] getAllEvents returning:", events.length, "events");
    return events;
  }

  /**
   * The authoritative server order id for a local aggregate, once reconciled.
   * Null while the order exists only on this device.
   */
  async getServerOrderId(aggregateId) {
    const { getOrderMapping } = await import("../db");
    return await getOrderMapping(aggregateId);
  }
}

export const meshService = new MeshService();
